import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CreateProjectDropdown from '../DropdownStyle/CreateProjectDropdown';

/**
 * A reusable dropdown component with checkbox selection
 * @param {Object} props Component props
 * @param {Array} props.options Array of options with label and value
 * @param {Array} props.selectedOptions Currently selected options
 * @param {Function} props.onChange Callback when selection changes
 * @param {string} props.placeholder Placeholder text when nothing is selected
 * @param {Object} props.styles Additional styles to apply
 * @param {string} props.label Label to display for the dropdown
 * @param {boolean} props.error Whether there's a validation error
 * @param {string} props.errorMessage Error message to display
 * @param {string} props.direction Dropdown direction: 'down' (default) or 'up'
 */
const CustomCheckboxDropdown = ({
  options = [],
  selectedOptions = [],
  onChange,
  placeholder = 'Select options',
  styles = {},
  label,
  error = false,
  errorMessage,
  countLabel = 'items_selected',
  testId = 'custom-checkbox-dropdown',
  direction = 'down',
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOptionSelection = option => {
    const isSelected = selectedOptions.some(o => o.value === option.value);
    let newSelection;

    if (isSelected) {
      newSelection = selectedOptions.filter(o => o.value !== option.value);
    } else {
      newSelection = [...selectedOptions, option];
    }

    onChange(newSelection);
  };

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div data-testid={testId} style={{ width: '100%' ,}}>
      {label && <div style={{ marginBottom: '8px' }}>{label}</div>}
      <div
        ref={dropdownRef}
        style={{ position: 'relative', ...styles.container }}
      >
        <button
          type="button"
          onClick={toggleDropdown}
          style={{
            ...CreateProjectDropdown().control(
              {},
              { menuIsOpen: isDropdownOpen },
            ),
            ...(error && {
              border: '1px solid #fe5969',
              backgroundColor: 'rgba(252, 87, 104, 0.05)',
            }),
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            ...styles.button,
          }}
        >
          {selectedOptions.length > 0
            ? `${selectedOptions.length} ${t(countLabel)}`
            : t(placeholder)}
        </button>
        {isDropdownOpen && (
          <div
            style={{
              ...CreateProjectDropdown().menu({}, { menuIsOpen: true }),
              position: 'absolute',
              ...(direction === 'up'
                ? { bottom: '100%', marginBottom: '8px' }
                : { top: '100%', marginTop: '8px' }),
              left: 0,
              width: '100%',
              maxHeight: '200px',
              overflowY: 'auto',
              backgroundColor: '#fff',
              zIndex: 1000,
              ...styles.menu,
            }}
          >
            {options.map(option => (
              <div
                key={option.value}
                style={{
                  ...CreateProjectDropdown().option(
                    {},
                    {
                      isSelected: selectedOptions.some(
                        o => o.value === option.value,
                      ),
                    },
                  ),
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  cursor: 'pointer',
                  marginTop: '5px',
                  marginBottom: '5px',
                  ...styles.option,
                }}
                onClick={() => handleOptionSelection(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && errorMessage && (
        <div style={{ color: '#fe5969', fontSize: '12px', marginTop: '4px' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default CustomCheckboxDropdown;
